// ===== Packages =====

import React, {
    useRef,
    useState,
    useEffect,
}                                       from 'react';
import {
    useNavigate,
    useLocation,
}                                       from 'react-router-dom';
import { ReactSVG }                     from 'react-svg';

// ===== Hooks =====

import {
    useTimeout,
}                                       from '../../hooks';

// ===== Enums =====

import {
    CURSOR_TARGET,
    INTERACTABLE_OBJECT,
    PAGE_ROUTE,
    USER_ACTION_TYPE,
}                                       from '../../enums';

// ===== Services =====

import {
    recordUserAction,
    updatePageTitle,
    getPressEventHandlers,
    getTouchMouseEventHandlers,
    isTestMode,
}                                       from '../../services';

// ===== Images =====

import VerascopeLogo                    from '../../images/verascope-logo-silhouette.svg';

// ===== Constants =====

import {
    HOVER_TARGET_CLASSNAME,
    PAGE_SUBTITLE_TRANSITION_DURATION,
    SUPPORT_EMAIL_ADDRESS,
}                                       from '../../constants/generalConstants';
import CURSOR_SIGN                      from '../../constants/cursorSigns';

// ===== Styles =====

import {
    Container,
    ColorText,
    TermsHeader,
    TermsList,
    TermsListItem,
    TermsText,
    TermsTextContainer,
    Anchor,
}                                       from './styles';
import {
    PageLogo,
    PageSubtitle,
    PageTitle,
}                                       from '../../styles';
import { theme }                        from '../../themes/theme-context';

interface Props {
    customCursorActive: boolean,
    currentSessionId: string | null,
    onCursorEnter: (
        targetType: CURSOR_TARGET | INTERACTABLE_OBJECT | string,
        actions: string[],
        candidateTarget?: HTMLElement,
    ) => void,
    onCursorLeave: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
}
function TermsView({
    customCursorActive,
    currentSessionId,
    onCursorEnter,
    onCursorLeave,
}: Props): JSX.Element {
    // ===== React Router =====

    const navigate = useNavigate();
    const location = useLocation();

    // ===== State =====

    const [showText, setShowText] = useState<boolean>(false);
    // Stores the location key for which a view action has been recorded.
    const recordedViewPageUserActionKey = useRef<string | null>(null);

    // ===== Animation Constants =====

    const TERMS_TEXT_DURATION = 400;
    const HEADER_DELAY_DURATION = PAGE_SUBTITLE_TRANSITION_DURATION - 150;

    // ===== Methods =====

    const onLogoEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.logomark,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onLogoLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    const onLogoClick = async (e: React.MouseEvent): Promise<void> => {
        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.clickHomeButton,
                sessionId: currentSessionId,
                payload: {
                    route: PAGE_ROUTE.about,
                },
            });
        }
        onCursorLeave(e);
        navigate(
            `/${PAGE_ROUTE.landing}`,
            {
                state: {
                    prevPath: location.pathname,
                },
            },
        );
    };

    /**
     * Records user action of viewing terms page
     */
    const recordTermsPageUserAction = async (): Promise<void> => {
        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.viewTermsPage,
                sessionId: currentSessionId,
            });
        }
    };

    // ===== Side Effects =====

    /**
     * Manages page title changes
     */
    useEffect(() => {
        updatePageTitle(
            'Terms',
        );
    }, []);

    /**
     * Records View Terms Page User Action
     */
    useEffect(() => {
        if (
            !currentSessionId
            || recordedViewPageUserActionKey.current === location.key
        ) return;
        recordTermsPageUserAction();
        recordedViewPageUserActionKey.current = location.key;
    }, [
        currentSessionId,
        location.key,
    ]);

    // Show text
    useEffect(() => {
        clearTimeoutShowText();
        timeoutShowText();
    }, []);

    const {
        start: timeoutShowText,
        clear: clearTimeoutShowText,
    } = useTimeout(() => {
        setShowText(true);
    }, HEADER_DELAY_DURATION);

    return (
        <Container>
            <PageLogo
                dark
                withEnterTransition
                className={HOVER_TARGET_CLASSNAME}
                customCursorActive={customCursorActive}
                {...getTouchMouseEventHandlers({
                    onMouseEnter: (e) => onLogoEnter(e),
                    onMouseLeave: (e) => onLogoLeave(e),
                })}
                {...getPressEventHandlers({ onMouseDown: onLogoClick })}
            >
                <ReactSVG
                    src={VerascopeLogo}
                />
            </PageLogo>
            <PageTitle
                withEnterTransition
                color={theme.verascopeColor.purple200}
            >
                TERMS OF USE
            </PageTitle>
            <PageSubtitle
                withEnterTransition
                color={theme.verascopeColor.purple400}
            >
                Last Updated: August 19, 2025
            </PageSubtitle>
            <TermsTextContainer>
                <TermsText
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple500}>Welcome to Verascope! </ColorText>
                    These Terms of Use (“
                    <ColorText color={theme.verascopeColor.purple500}>Terms</ColorText>
                    ”) govern your access to and use of the website, digital content, and services provided by Verascope, Inc. (collectively, the “
                    <ColorText color={theme.verascopeColor.purple500}>Services</ColorText>
                    ”). By accessing our website or using any Services, you (“
                    <ColorText color={theme.verascopeColor.purple500}>User</ColorText>
                    ” or “
                    <ColorText color={theme.verascopeColor.purple500}>you</ColorText>
                    ”) agree to be bound by these Terms and our
                    <Anchor
                        className={HOVER_TARGET_CLASSNAME}
                        customCursorActive={customCursorActive}
                        href={`${window.location.origin}/${PAGE_ROUTE.privacyPolicy}/`}
                        target="_blank"
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: (e) => onCursorEnter(
                                CURSOR_TARGET.email,
                                [CURSOR_SIGN.click],
                                e.currentTarget as HTMLElement,
                            ),
                            onMouseLeave: (e) => onCursorLeave(e),
                        })}
                    >
                        {' '}
                        Privacy Policy
                        {' '}
                    </Anchor>
                    (which is incorporated by reference). If you do not agree with these
                    Terms, you should not use our Services.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    Please read these Terms carefully to understand your rights and obligations. We have tried to write these Terms in clear language
                    for ease of understanding. If you have any questions, feel free to contact us.
                </TermsText>
                <TermsHeader
                    marginTop
                    color={theme.verascopeColor.purple400}
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    1. Eligibility and Age
                </TermsHeader>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    Our website content (including our digital book) is family-friendly and intended for a general audience. We do
                    <ColorText color={theme.verascopeColor.purple500}> not impose a strict age restriction </ColorText>
                    for accessing most of our content – the book is suitable for
                    <ColorText color={theme.verascopeColor.purple500}> all ages</ColorText>
                    . However, by using certain features of the Services (such as creating an account or making a purchase), you
                    represent and warrant that you have the legal capacity to enter into this agreement. In particular,
                    <ColorText color={theme.verascopeColor.purple500}> if you are under the age of 18</ColorText>
                    , you should use our Services
                    <ColorText color={theme.verascopeColor.purple500}> only with the involvement or consent of a parent or legal guardian</ColorText>
                    .
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    Minors under 18 are encouraged to have a parent/guardian review these Terms with them.
                    <ColorText color={theme.verascopeColor.purple500}> Children under 13 </ColorText>
                    are not permitted to create accounts or provide personal information on our site without parental consent, in
                    compliance with U.S. COPPA and similar laws. While we allow under-13s to view the publicly available parts of
                    the site (since our content is not harmful), features like account registration, contributing annotations, or
                    making purchases are intended for users 13 or older.
                    <ColorText color={theme.verascopeColor.purple500}>
                        {' '}
                        Verascope does not knowingly allow unsupervised account creation by children under 13.
                        {' '}
                    </ColorText>
                    (See our
                    <Anchor
                        className={HOVER_TARGET_CLASSNAME}
                        customCursorActive={customCursorActive}
                        href={`${window.location.origin}/${PAGE_ROUTE.privacyPolicy}/`}
                        target="_blank"
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: (e) => onCursorEnter(
                                CURSOR_TARGET.email,
                                [CURSOR_SIGN.click],
                                e.currentTarget as HTMLElement,
                            ),
                            onMouseLeave: (e) => onCursorLeave(e),
                        })}
                    >
                        {' '}
                        Privacy Policy
                    </Anchor>
                    ’s Children’s section for more details on how we handle minors’ data.)
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    By using the Services, you affirm that you either (a) are at least 18 years old, or (b) if you are between 13
                    and 18 (or the age of majority in your jurisdiction), you are using the site with parental/guardian permission
                    and supervision. Verascope reserves the right to ask for proof of parental consent for users under 18 if needed,
                    or to disable accounts that are found to be in violation of age requirements.
                </TermsText>
                <TermsHeader
                    marginTop
                    color={theme.verascopeColor.purple400}
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    2. Accounts and User Registration
                </TermsHeader>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Creating an Account: </ColorText>
                    In order to access certain features (such as purchasing items or leaving annotations), you must create a user
                    account. You can register in several ways:
                </TermsText>
                <TermsList>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Third-Party Login: </ColorText>
                        You may sign up using an existing Google, Facebook, GitHub, or X/Twitter account. If you choose a social login,
                        we will receive some basic information from that account (like your email and name) to create your Verascope
                        profile. You are responsible for ensuring that your use of any third-party account complies with that provider’s terms.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Email and Password: </ColorText>
                        Alternatively, you can sign up with an email address and password. If you do so, we will send a verification
                        link to the email you provide – you must click that link to verify your email before your account becomes fully active.
                    </TermsListItem>
                </TermsList>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Account Information: </ColorText>
                    You agree to provide truthful, accurate, and complete information during registration and to keep it updated. You may
                    not impersonate someone else or create an account using someone else’s information or email.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>One Account: </ColorText>
                    Each user should have only one account (unless expressly permitted otherwise). You may not create multiple accounts
                    to bypass restrictions or gain additional benefits.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Account Security: </ColorText>
                    You are responsible for maintaining the
                    <ColorText color={theme.verascopeColor.purple500}> confidentiality of your account login credentials </ColorText>
                    and for all activities that occur under your account.
                    <ColorText color={theme.verascopeColor.purple500}> Keep your username and password secure </ColorText>
                    and do not share them with others. If you use a social login, ensure your social account is properly secured as
                    well. You agree to
                    <ColorText color={theme.verascopeColor.purple500}> notify us immediately </ColorText>
                    at
                    {' '}
                    <Anchor
                        className={HOVER_TARGET_CLASSNAME}
                        customCursorActive={customCursorActive}
                        href={`mailto:${SUPPORT_EMAIL_ADDRESS}`}
                        target="_blank"
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: (e) => onCursorEnter(
                                CURSOR_TARGET.email,
                                [CURSOR_SIGN.click],
                                e.currentTarget as HTMLElement,
                            ),
                            onMouseLeave: (e) => onCursorLeave(e),
                        })}
                    >
                        {SUPPORT_EMAIL_ADDRESS}
                    </Anchor>
                    {' '}
                    if you suspect any unauthorized access to or use of your account, or any breach of security.
                    Verascope will not be liable for any loss or damage arising from your failure to safeguard your credentials.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Account Use: </ColorText>
                    You must not use another person’s account without permission. Similarly, you should not allow others to use your account.
                    We reserve the right to require password resets or other actions to maintain account security if we detect suspicious activity.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Profile Settings: </ColorText>
                    In your account dashboard, you can manage certain settings such as your display name, and email preferences
                    (including opting out of  the mailing list if you wish). You are responsible for any information or content
                    you add to your profile.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Account Termination: </ColorText>
                    You may terminate your own account at any time by contacting us (see Section 12). We also reserve
                    the right to suspend or terminate your account as described in Section 8 (Termination) if you violate these Terms or for security
                    reasons.
                </TermsText>
                <TermsHeader
                    marginTop
                    color={theme.verascopeColor.purple400}
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    3. User Conduct and Acceptable Use
                </TermsHeader>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    We want all users to have a positive experience on Verascope. By accessing our Services, you agree to
                    <ColorText color={theme.verascopeColor.purple500}> use the site in a lawful and respectful manner</ColorText>
                    . You specifically
                    <ColorText color={theme.verascopeColor.purple500}> agree NOT to:</ColorText>
                </TermsText>
                <TermsList>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Violate Laws: </ColorText>
                        Do not use the platform for any purpose that is illegal or prohibited by these Terms. You must comply with all
                        applicable local, state, national, and international laws and regulations. This includes not using our site in
                        any way to facilitate unlawful transactions or activities.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Abuse, Harass, or Harm Others: </ColorText>
                        Do not post or share any content that is
                        <ColorText color={theme.verascopeColor.purple500}> discriminatory, hateful, harassing, threatening, or defamatory </ColorText>
                        towards others. You also agree not to advocate violence or harm against any group or individual. Content that
                        is abusive, obscene, or
                        <ColorText color={theme.verascopeColor.purple500}> pornographic/offensive </ColorText>
                        is strictly prohibited (note: the book content itself is appropriate for all ages, and we expect user
                        contributions to be as well).
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Post Illegal or Infringing Content: </ColorText>
                        Do not upload or post content (including in annotations or any future forums) that you do not have the right to
                        share. This means
                        <ColorText color={theme.verascopeColor.purple500}> no posting of others’ copyrighted material </ColorText>
                        (text, images, audio, etc.) without permission, and no sharing of content that violates intellectual property
                        rights, privacy rights, or any other rights of third parties. Also, you must not post any content that contains
                        child exploitation, extreme violence, or any unlawful material.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Spam or Unauthorized Advertising: </ColorText>
                        Do not use our Services to send
                        <ColorText color={theme.verascopeColor.purple500}>
                            {' '}
                            junk mail, spam, chain letters, pyramid schemes, or any form of unsolicited mass messaging
                        </ColorText>
                        . The annotation feature and any communication tools on our site are meant for discussion relevant to the book
                        content; they are
                        <ColorText color={theme.verascopeColor.purple500}> not </ColorText>
                        to be used for promoting products, services, or external websites. Similarly, do not scrape
                        our user base for emails or contact info to send unsolicited messages.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Misuse Personal Data: </ColorText>
                        You agree not to harvest, scrape, or collect other users’ personal information from our site without consent.
                        Also, do not reveal another person’s personal information (like posting someone’s real name, address, email,
                        or other data) through our Services. This includes refraining from doxxing or exposing someone’s identity if
                        they use a pseudonym on the site.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Security Interference: </ColorText>
                        Do not attempt to interfere with the proper working of our website. This means no introducing viruses, worms,
                        malware, or any code of a destructive nature. Do not attempt to hack, DDOS, or disrupt the Service.
                        Additionally, avoid any attempt to circumvent measures we use to restrict access to content (for example,
                        attempting to access paid content without purchase, or using bots to download content).
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Impersonation and Misrepresentation: </ColorText>
                        Do not impersonate any person or entity, or falsely state or otherwise misrepresent your affiliation with a
                        person or entity. You also must not mislead others as to the origin of any content you post (for instance, do
                        not leave annotations under someone else’s name or under a false identity).
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Automated Use: </ColorText>
                        You will not use any automated system (such as scripts, bots, or scrapers) to access the Services in a manner
                        that sends more request messages to our servers than a human can reasonably produce in the same period.
                        Basically, don’t overload our Service with non-human usage. Indexing by legitimate search engines is allowed
                        if done in accordance with our robots.txt file, but other scraping or crawling is not permitted without our prior consent.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Reverse Engineering: </ColorText>
                        You must not decompile, reverse engineer, or otherwise attempt to obtain the source code or underlying ideas
                        of any part of our Service (except as permitted by law). Similarly, you agree not to attempt to bypass any content
                        protection or digital rights management features we have in place for the digital book or other content.
                    </TermsListItem>
                </TermsList>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    If you engage in any of the above prohibited activities, it is a breach of these Terms and may result in immediate
                    suspension or termination of your account and access, as well as potential legal action if warranted. We reserve the
                    right to remove or edit any user content that violates these rules or that we find objectionable in our sole discretion.
                </TermsText>
                <TermsHeader
                    marginTop
                    color={theme.verascopeColor.purple400}
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    4. User Content and Annotations
                </TermsHeader>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>User-Generated Content: </ColorText>
                    Our Services allow users who have purchased the digital book to create and share
                    <ColorText color={theme.verascopeColor.purple500}> annotations </ColorText>
                    on the book’s content. “Annotations” include any commentary, notes, highlights, or other content (text, images, audio
                    notes) that you add to the digital book through the annotation feature. All content that users post or upload via our
                    Services is collectively referred to as “User Content.”
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    By posting User Content on our Service, you agree to the following:
                </TermsText>
                <TermsList>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Ownership: </ColorText>
                        You retain any intellectual property rights you hold in the content you create. For example, if you write an original
                        comment or upload an image you created in an annotation, you retain copyright in those contributions.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>License to Verascope: </ColorText>
                        You grant Verascope a non-exclusive, worldwide, royalty-free, perpetual license to use, display, reproduce,
                        distribute, adapt, and modify your User Content
                        <ColorText color={theme.verascopeColor.purple500}> for the purpose of operating and providing the Services</ColorText>
                        . This license allows us to show your annotations to other users (e.g., other readers of the book will see your
                        comments and vice versa), to moderate or format the content as needed (for example, if we need to convert an image
                        thumbnail or transcode an audio note for playback), and to include your content in data backups or future versions of
                        the Service. This license is
                        <ColorText color={theme.verascopeColor.purple500}> limited </ColorText>
                        – we are not claiming ownership of your contributions, and we will not sell your specific comments or use them outside
                        of the Service’s context without your permission.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Visibility: </ColorText>
                        Annotations are by default shared with all users who have access to that portion of the book.
                        <ColorText color={theme.verascopeColor.purple500}> Do not include private or sensitive information </ColorText>
                        about yourself or others in your annotations, because they are not private. If you wish to take personal notes, assume that
                        anything you write could be seen by others who have the book. We are not responsible for what other users do with information
                        you make public through your content.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Appropriate Content: </ColorText>
                        All User Content must adhere to the
                        <ColorText color={theme.verascopeColor.purple500}> User Conduct </ColorText>
                        rules in Section 3. This means your annotations should remain on-topic (related to the book or discussion around it),
                        respectful, and lawful. You must not include content in annotations that is prohibited (hate speech, pirated text from
                        elsewhere, advertisements, etc.). We particularly emphasize
                        <ColorText color={theme.verascopeColor.purple500}> no offensive or adult content </ColorText>
                        in annotations since the book is for all ages.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>No Infringement: </ColorText>
                        Do not include material in your annotations that you don’t have permission to use. For example, if you quote a long passage
                        from another book or upload an image you found on the internet, ensure it’s either public domain, under a suitable license,
                        or you have the rights/permission. You are responsible if you post copyrighted content without permission. Verascope
                        complies with the
                        <ColorText color={theme.verascopeColor.purple500}> Digital Millennium Copyright Act (DMCA)</ColorText>
                        , and we will remove content that is claimed to infringe someone’s copyright as described in Section 5 below.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>No Misuse of Annotations: </ColorText>
                        The annotation feature is provided to enrich the reading experience. It should not be used for irrelevant content. Do not
                        spam annotations across pages, do not use annotations to harass others (e.g., replying to someone’s annotation with insults
                        or irrelevant content), and do not flood a chapter with repetitive notes. We reserve the right to limit posting frequency or
                        length of annotations if needed to prevent abuse.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Moderation: </ColorText>
                        Verascope may (but has no obligation to) monitor and moderate user annotations and other content. We reserve the right to
                        remove or edit any User Content that violates these Terms or for any other valid reason. We also may respond to user
                        reports – if you see an annotation that you believe violates our policies or is otherwise objectionable, you can report
                        it (through any provided reporting mechanism or by contacting us). However, please note that literary critique, differing
                        opinions, or commentary you disagree with are not in themselves violations; we will not remove content simply because it is
                        critical or controversial if it doesn’t violate specific rules.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>User Responsibility: </ColorText>
                        You are solely responsible for the content you post. Verascope is not liable for any user content, nor do we guarantee the
                        truthfulness or reliability of user content. Opinions expressed in annotations belong to the users who posted them, not to
                        Verascope. If you rely on information in someone else’s annotation, you do so at your own risk.
                    </TermsListItem>
                </TermsList>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    By creating content on our site, you agree to indemnify Verascope for any claims resulting from your content (see Section 11 on
                    Indemnification).
                </TermsText>
                <TermsHeader
                    marginTop
                    color={theme.verascopeColor.purple400}
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    5. Intellectual Property of Verascope
                </TermsHeader>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Verascope Content: </ColorText>
                    The content provided by Verascope through the Services, including but not limited to the
                    <ColorText color={theme.verascopeColor.purple500}> digital book and its chapters</ColorText>
                    , text, graphics, logos, icons, images, audio clips, video clips, digital downloads, and software, is the property of Verascope,
                    Inc. or our content suppliers and is protected by intellectual property laws (such as copyright, trademark, and database rights).
                    This includes the compilation of content on the site and the software and architecture that powers the Service.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Limited License: </ColorText>
                    Verascope grants you a limited, non-transferable, non-exclusive, revocable license to access and
                    <ColorText color={theme.verascopeColor.purple500}> make personal, non-commercial use </ColorText>
                    of the Verascope content and Services. This means:
                </TermsText>
                <TermsList>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        You may read and view the digital book previews for free, and if you purchase chapters or the full book, you may
                        access those for your own personal reading and study.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        You may not copy, reproduce, distribute, or create derivative works from the book or other content on the site,
                        except as allowed by law (such as brief quotations under fair use/fair dealing) without explicit permission from us.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>No commercial exploitation: </ColorText>
                        You cannot resell any portion of our site or its content, or use our content (including the book text and images)
                        for any commercial purpose without our prior written consent.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        You must not remove or alter any copyright notices, watermarks, or other proprietary marks on the content.
                    </TermsListItem>
                </TermsList>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Digital Book Protection: </ColorText>
                    We provide the digital book content through our platform. Except for purchased content which you have a license to read,
                    you do
                    <ColorText color={theme.verascopeColor.purple500}> not acquire any ownership of the book or other content</ColorText>
                    . Purchasing digital content grants you access rights but not ownership of the underlying intellectual property. You agree
                    not to attempt to download, screenshot, or otherwise copy large portions of the book text. Any attempt to mass-copy or
                    redistribute the book (including sharing your login with others who haven’t purchased it, printing and distributing
                    copies, etc.) is a violation of these Terms and may infringe our copyrights.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Trademarks: </ColorText>
                    “Verascope” and our logos, slogans, or design elements that we use in our Service are trademarks or trade dress of
                    Verascope, Inc. You may not use Verascope’s name or branding in any way that confuses consumers or disparages us.
                    All other product or company names mentioned on the site (e.g., Stripe, Facebook, etc.) are the trademarks of their
                    respective owners.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Feedback: </ColorText>
                    If you submit suggestions, ideas, or feedback to us about the Service (for example, bug reports or feature requests),
                    you agree that we can use and share such feedback for any purpose without compensating you. We appreciate feedback
                    but are not obligated to implement it.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Copyright Infringement Policy (DMCA): </ColorText>
                    Verascope respects intellectual property rights and will respond to notices of alleged copyright infringement in
                    accordance with the Digital Millennium Copyright Act. If you are a copyright owner or an agent thereof and believe
                    that any content on our site (including User Content) infringes upon your copyrights, you may submit a notification
                    to us by providing the following information in writing:
                </TermsText>
                <TermsList
                    isNumbered
                >
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        Identification of the copyrighted work claimed to have been infringed (or if multiple works, a representative list).
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        Identification of the material that is claimed to be infringing and information reasonably sufficient to permit us to
                        locate the material on our Service (e.g., the URL of the page where the content is).
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        Contact information for the complaining party (address, telephone number, and email).
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        A statement that the complaining party has a good faith belief that use of the material is not authorized by the copyright
                        owner, its agent, or the law.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        A statement that the information in the notification is accurate, and
                        <ColorText color={theme.verascopeColor.purple500}> under penalty of perjury</ColorText>
                        , that the complaining party is authorized to act on behalf of the owner of an exclusive right that is allegedly infringed.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        A physical or electronic signature of a person authorized to act on behalf of the owner.
                    </TermsListItem>
                </TermsList>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    You can send DMCA notices to our designated copyright agent at
                    {' '}
                    <Anchor
                        className={HOVER_TARGET_CLASSNAME}
                        customCursorActive={customCursorActive}
                        href={`mailto:${SUPPORT_EMAIL_ADDRESS}`}
                        target="_blank"
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: (e) => onCursorEnter(
                                CURSOR_TARGET.email,
                                [CURSOR_SIGN.click],
                                e.currentTarget as HTMLElement,
                            ),
                            onMouseLeave: (e) => onCursorLeave(e),
                        })}
                    >
                        {SUPPORT_EMAIL_ADDRESS}
                    </Anchor>
                    {' '}
                    (Attn: Copyright Agent). We reserve the right to remove content alleged to be infringing without
                    prior notice and at our sole discretion, and to terminate the accounts of repeat infringers in accordance with the DMCA
                    and other applicable laws.
                </TermsText>
                <TermsHeader
                    marginTop
                    color={theme.verascopeColor.purple400}
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    6. Purchases, Pricing, and Payments
                </TermsHeader>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>E-commerce Features: </ColorText>
                    Verascope’s Services include an online store where logged-in users can purchase certain products:
                </TermsText>
                <TermsList>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Digital Content: </ColorText>
                        Individual book chapters or the entire digital book can be purchased for online access.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Physical Goods: </ColorText>
                        We offer merchandise such as shirts, caps, necklaces, keychains, and physical copies of the book.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        We may also offer other items or services from time to time as listed on our site
                    </TermsListItem>
                </TermsList>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    In order to make any purchase, you
                    <ColorText color={theme.verascopeColor.purple500}> must have a user account </ColorText>
                    (see Section 2) and be logged in. This ensures we can associate your purchases with you (for digital access and order history).
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Pricing: </ColorText>
                    Prices for all products (digital or physical) are listed on our website and are subject to change. We do our best to ensure
                    that the prices displayed are correct. In the event of a pricing error (typo or technical issue), we reserve the right to cancel
                    any orders for items listed at an incorrect price. If we do so, we will notify you and you will not be charged (or we will issue
                    a refund if the charge went through).
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Negotiation Option: </ColorText>
                    For the digital book and chapters, we provide an
                    <ColorText color={theme.verascopeColor.purple500}> optional negotiation feature</ColorText>
                    : if you would like to propose a different price, you can send an email to our sales team. This does not guarantee any discount or
                    special pricing, but we are open to discussion. Any negotiated pricing would be provided in writing (via email) and would
                    apply only if confirmed by us. We reserve the right to decline any proposed alternate price. The negotiation is done in good faith
                    via email, and until a new price is agreed upon, the standard listed price applies. This feature is meant to accommodate special
                    circumstances or bulk purchases, etc., and is not a general haggling on every sale.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Payment Processing: </ColorText>
                    All payments are processed securely by
                    <ColorText color={theme.verascopeColor.purple500}> Stripe</ColorText>
                    . Accepted payment methods include major credit and debit cards (and may include other methods like Apple Pay or Google Pay if
                    enabled by Stripe on our checkout). When you make a purchase, you will be redirected to or presented with a Stripe-powered payment
                    form. By providing your payment information, you authorize us (and Stripe, on our behalf) to charge the specified amount to your
                    payment method. You also authorize us to collect and store your shipping address and other necessary information to facilitate
                    the transaction.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    If you opt to save your card for future use, Stripe will store your card details and provide us with a token.
                    <ColorText color={theme.verascopeColor.purple500}> We do not store your full card details </ColorText>
                    – only a token and perhaps the last4 digits and expiration (for display) are stored by us. Future charges using a saved card will
                    use that token. You can remove your saved card at any time by contacting us.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Order Confirmation: </ColorText>
                    After a successful purchase, you will receive an email confirmation summarizing your order. For digital content,
                    your account will be updated to grant access immediately (or as soon as you next log in). For physical goods, we will process
                    the order for shipping.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Shipping (Physical Goods): </ColorText>
                    If you purchase physical merchandise or a physical book:
                </TermsText>
                <TermsList>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Shipping Address: </ColorText>
                        You must provide a valid shipping address. Please double-check that the address is correct and deliverable. We are
                        not responsible for orders shipped to an incorrect address provided by the user.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Shipping Charges: </ColorText>
                        Shipping costs will be calculated and shown to you at checkout (likely via Shippo’s rate calculation). These costs vary
                        depending on destination and shipping method. You agree to pay any shipping and handling charges shown at checkout.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>International Orders: </ColorText>
                        If we allow international shipping, be aware you are responsible for any customs duties,
                        taxes, or import fees that may apply in your country. We will typically mark the value and contents of the package
                        accurately; any additional charges at customs are beyond our control.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Delivery: </ColorText>
                        We will provide an estimated delivery timeframe, but actual delivery is handled by third-party carriers. We will email
                        you a tracking number when available. Once we hand the package to the carrier, the risk of loss passes to you (we will of
                        course help if a package is lost in transit, but final liability may lie with the carrier).
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Problems: </ColorText>
                        If an item arrives damaged or is lost in transit, please contact us so we can work with you on a solution
                        (replacement or other remedy). While our
                        <ColorText color={theme.verascopeColor.purple500}> official policy is no refunds </ColorText>
                        (see Section 7), we do want you to get the product in good condition. We’ll handle such issues on a case-by-case basis.
                    </TermsListItem>
                </TermsList>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Taxes: </ColorText>
                    All prices are listed
                    <ColorText color={theme.verascopeColor.purple500}> exclusive of any applicable taxes</ColorText>
                    , unless stated otherwise. If any sales tax, VAT, or similar tax is applicable to your purchase, it will be calculated at checkout
                    and added to your total. You are responsible for paying any such taxes.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Stripe Terms: </ColorText>
                    By making a purchase, you also agree to Stripe’s terms and conditions to the extent they apply as the payment processor. Stripe or
                    your bank may impose limits, holds, or fraud prevention measures that could delay processing. Ensure your payment information is
                    accurate and that you have sufficient funds or credit. We are not responsible for payment failures due to issues beyond our
                    control (such as declined cards or network outages).
                </TermsText>
                <TermsHeader
                    marginTop
                    color={theme.verascopeColor.purple400}
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    7. No Refunds Policy
                </TermsHeader>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>All Sales Are Final: </ColorText>
                    Please choose carefully when making a purchase, as
                    <ColorText color={theme.verascopeColor.purple500}>
                        {' '}
                        Verascope maintains a strict no-refund policy on all purchases, whether digital or physical
                    </ColorText>
                    . Once a purchase is completed, it is non-refundable and non-returnable. This applies to:
                </TermsText>
                <TermsList>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Digital purchases: </ColorText>
                        Once you have been granted access to a digital chapter or the full book, we cannot revoke your
                        knowledge or access to that content, so we do not offer refunds for digital content.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Physical products: </ColorText>
                        We do not offer refunds or returns on merchandise or physical books, except in cases of
                        defective or incorrect items (see below).
                    </TermsListItem>
                </TermsList>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    By purchasing any item on our site, you acknowledge and agree that you are not entitled to a refund
                    or exchange of any kind, under any circumstances.
                    <ColorText color={theme.verascopeColor.purple500}> Verascope does not offer any money-back guarantees.</ColorText>
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Exceptions: </ColorText>
                    The only exceptions to this no-refund policy are if we are required by applicable law to accept a return or provide a
                    refund (such as certain consumer rights that may apply in your jurisdiction), or if we explicitly communicate a special
                    guarantee for a particular product. As of now, no such guarantees are in place. However, as noted in Section 6, if
                    you receive a
                    <ColorText color={theme.verascopeColor.purple500}> damaged or defective physical product</ColorText>
                    , contact us within 14 days of delivery with details and photos of
                    the issue. We may, at our discretion, send a replacement or work out a solution, but we generally do not issue refunds.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Chargebacks: </ColorText>
                    Attempting to circumvent our no-refund policy by initiating a chargeback through your bank for a legitimate purchase is
                    a violation of these Terms. If you have an issue with a purchase, please contact us first to resolve it. Unauthorized
                    chargebacks for valid charges may result in us terminating your account and pursuing the owed amount (plus any fees
                    incurred) to the fullest extent permitted by law.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    We stand by the quality of our offerings and provide ample information (previews of chapters, product descriptions, etc.)
                    to help you make informed decisions. If you have questions about a product, please ask before purchasing.
                </TermsText>
                <TermsHeader
                    marginTop
                    color={theme.verascopeColor.purple400}
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    8. Termination and Suspension
                </TermsHeader>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>By You: </ColorText>
                    You have the right to stop using our Services at any time. You may also delete your account if you no longer wish to be a
                    registered user. If you wish to terminate your account, you can do so by contacting us.
                    Note that even if you delete your account, these Terms (and your agreement to them) remain applicable to your past use.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>By Us: </ColorText>
                    We reserve the right to suspend or terminate your access to the Services (or any portion thereof) at our discretion, with
                    or without notice, if:
                </TermsText>
                <TermsList>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        You violate any provision of these Terms or our policies (e.g., posting prohibited content, engaging in fraud, etc.).
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        We are required to do so to comply with a legal requirement or court order.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        We suspect your account has been compromised or used fraudulently.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        You engage in behavior that poses a security risk or could be harmful to other users or to Verascope.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        For prolonged inactivity (if a user hasn’t logged in for a very long time, we might disable the
                        account for security – we would generally warn first).
                    </TermsListItem>
                </TermsList>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    In cases of minor violations or disputes, we may opt to suspend your account (making it inaccessible) while
                    we investigate, rather than terminate outright. Suspension may also involve removing your ability to post
                    content or restricting certain features.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Effect of Termination: </ColorText>
                    If your account is terminated (by you or us), you will lose access to any features that require login. If you had
                    purchased digital content, you will no longer be able to access that content through our platform once the account
                    is gone. We recommend downloading/recording any receipts or information you might need before deleting your account.
                    Termination of your account does
                    <ColorText color={theme.verascopeColor.purple500}> not </ColorText>
                    relieve you of any obligation to pay any outstanding fees (if any), and it does not entitle you to any refund
                    (per our no-refund policy).
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    We also reserve the right to remove any User Content you have posted if we terminate your account for violation of
                    Terms. However, we may retain certain content or data as needed for legal compliance or evidence.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>User Initiated Content Removal: </ColorText>
                    If you wish to remove specific content you have posted (like an annotation), you can
                    delete it if the interface allows, or request our help to remove it. We will endeavor to honor such requests outside
                    of the scope of termination as well.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Survival: </ColorText>
                    The provisions of these Terms which by their nature should survive termination (such as intellectual property
                    rights, disclaimers, limitation of liability, indemnity, and governing law) will survive any termination or
                    expiration of these Terms.
                </TermsText>
                <TermsHeader
                    marginTop
                    color={theme.verascopeColor.purple400}
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    9. Third-Party Links and Services
                </TermsHeader>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    Our website may contain
                    <ColorText color={theme.verascopeColor.purple500}> links to third-party websites or services </ColorText>
                    that are not owned or controlled by Verascope. For example:
                </TermsText>
                <TermsList>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        The page listing job application opportunities might link out to an external form or a third-party recruitment platform.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        We might include links to our social media pages or references to other resources.
                    </TermsListItem>
                </TermsList>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    When you click on a third-party link and leave our site, you do so at your own risk.
                    <ColorText color={theme.verascopeColor.purple500}>
                        {' '}
                        Verascope is not responsible for the content or practices of any third-party websites
                        {' '}
                    </ColorText>
                    . Different terms and privacy policies may apply to your use of those sites or services. We do not endorse or assume any
                    responsibility for any third-party websites, information, materials, products, or services.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    Specifically, if you navigate to an external form for job applications or any other off-site content:
                </TermsText>
                <TermsList>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        Any information you provide there is subject to that third party’s terms.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        We encourage you to review the terms and privacy policies of any third-party site you visit.
                    </TermsListItem>
                </TermsList>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    We are also not responsible if a third-party link we provide is broken or not functioning, though
                    we appreciate being informed so we can update it.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    Your dealings with any third parties found on or through the Service are solely between you and that
                    third party. If you have any issues or disputes with such third parties (for example, if you follow a
                    link to a vendor’s site and buy something), you agree that Verascope is not liable or responsible for those issues.
                </TermsText>
                <TermsHeader
                    marginTop
                    color={theme.verascopeColor.purple400}
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    10. Disclaimer of Warranties
                </TermsHeader>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Use at Your Own Risk: </ColorText>
                    The Verascope Services, including our website, content, and any products sold, are provided on an “
                    <ColorText color={theme.verascopeColor.purple500}>as is</ColorText>
                    ” and “
                    <ColorText color={theme.verascopeColor.purple500}>as available</ColorText>
                    ” basis. To the fullest extent permitted by law, Verascope disclaims all warranties of any kind, whether express or
                    implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, title,
                    and non-infringement.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    We do
                    <ColorText color={theme.verascopeColor.purple500}> not guarantee </ColorText>
                    that:
                </TermsText>
                <TermsList>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        The Services will be uninterrupted, timely, secure, or error-free. While we strive for high availability, downtime
                        may occur, and we may have to perform maintenance.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        The information or content provided (whether by us or by users) is accurate, complete, or useful. We try our best
                        to ensure accuracy, particularly in our own content, but errors or omissions may occur. User-generated content is
                        not vetted for accuracy by us.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        Any defects or errors will be corrected, or that our site will be free of viruses or other harmful components
                        (although we make efforts on security, no system is foolproof).
                    </TermsListItem>
                </TermsList>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>No Warranty on Results: </ColorText>
                    We make no representation that using the Services will achieve any particular results. For example, we don’t promise that
                    reading our book will guarantee any outcome, or that participating in our community will meet your expectations.
                    <ColorText color={theme.verascopeColor.purple500}> Your use of the Services is at your sole risk</ColorText>
                    .
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Product Warranties: </ColorText>
                    Any products (like merchandise or the physical book) are provided as is, without any warranties unless explicitly stated.
                    We do not guarantee the durability or suitability of any product for a specific use. However, if an item is defective,
                    please reach out (as noted under purchases) and we’ll try to assist, but this is not a “warranty” promise, just customer service.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple500}>No Responsibility for Data Loss:  </ColorText>
                    While we aim to keep data (like annotations) safe, we cannot guarantee that your data will always be available.
                    You should keep backup copies of any information you deem important. We are not responsible for any data that might be
                    lost or unrecoverable (for instance, if a technical glitch erases some annotations or if we terminate a user who posted
                    content you valued).
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    Some jurisdictions do not allow the exclusion of certain warranties, so some of the above exclusions may not apply to
                    you. In such cases, our warranties are limited to the minimum extent permitted by applicable law.
                </TermsText>
                <TermsHeader
                    marginTop
                    color={theme.verascopeColor.purple400}
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    11. Limitation of Liability
                </TermsHeader>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    To the extent not prohibited by law, in no event shall Verascope, Inc. or its directors, officers, employees,
                    affiliates, agents, or licensors be liable for any
                    <ColorText color={theme.verascopeColor.purple500}> indirect, incidental, special, consequential, or punitive damages </ColorText>
                    arising out of or related to your use of (or inability to use) the Services. This includes, but is not limited to,
                    damages for lost profits, lost data, loss of goodwill, service interruption, computer damage, or system failure,
                    or the cost of substitute services, even if we have been advised of the possibility of such damages.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    In no event will Verascope’s total cumulative liability to you for all claims arising out of or relating to these
                    Terms or the Services exceed the
                    <ColorText color={theme.verascopeColor.purple500}>
                        {' '}
                        amount you have paid to Verascope in the 12 months prior to the event giving rise to the liability
                    </ColorText>
                    , or, if no purchase has been made, fifty U.S. dollars (USD $50). This limitation applies to all claims of liability
                    (e.g. whether based on warranty, contract, statute, tort (including negligence), or otherwise).
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    For example, we are not liable to you for any issues resulting from:
                </TermsText>
                <TermsList>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        Your implementation of advice or information from the book or from another user’s annotation.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        Interactions or disputes between you and other users.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        Unauthorized access to or alteration of your transmissions or data (though we aim to prevent this).
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        Third-party content or links on the site.
                    </TermsListItem>
                    <TermsListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={TERMS_TEXT_DURATION}
                    >
                        Any matter beyond our reasonable control (force majeure events like natural disasters, internet outages, etc.).
                    </TermsListItem>
                </TermsList>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Indemnification: </ColorText>
                    You agree to
                    <ColorText color={theme.verascopeColor.purple500}> indemnify, defend, and hold harmless </ColorText>
                    Verascope, Inc. and its affiliates, officers, agents, and employees from and against any and all claims, liabilities,
                    damages, losses, and expenses (including reasonable attorney’s fees and costs) that arise from or relate to your use or
                    misuse of the Services, your violation of these Terms, or your violation of any rights of a third party (including
                    intellectual property or privacy rights). This means if someone else sues us or demands compensation because of something
                    you did on our site (like posting infringing material or violating someone’s rights), you will cover our costs and damages.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    Some jurisdictions do not allow certain limitations of liability, so portions of this section may not apply to you.
                    In such cases, our liability is limited to the fullest extent permitted by law.
                </TermsText>
                <TermsHeader
                    marginTop
                    color={theme.verascopeColor.purple400}
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    12. Governing Law and Dispute Resolution
                </TermsHeader>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Governing Law: </ColorText>
                    These Terms and any dispute arising out of or related to these Terms or the Services will be governed by and
                    construed in accordance with the laws of the State of New York, USA,
                    <ColorText color={theme.verascopeColor.purple500}> without regard to its conflict of laws principles</ColorText>
                    . However, if you are accessing the Service from outside the USA, other mandatory consumer protection laws in your
                    country of residence may also apply for your benefit to the extent those laws cannot be waived.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Jurisdiction and Venue: </ColorText>
                    You agree that any dispute or claim arising out of or relating to these Terms or your use of the Services that
                    cannot be resolved amicably will be resolved exclusively in the state or federal courts located in New York
                    County, New York, USA. You and Verascope consent to the personal jurisdiction of these courts for litigation of any such dispute.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Waiver of Class Actions: </ColorText>
                    To the extent permissible by law,
                    <ColorText color={theme.verascopeColor.purple500}>
                        {' '}
                        you and Verascope agree that any disputes will be resolved only on an individual basis
                        {' '}
                    </ColorText>
                    and not in a class, consolidated, or representative action. You waive any right to participate in a class action
                    lawsuit or class-wide arbitration against Verascope.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Arbitration (if applicable): </ColorText>
                    (This is not explicitly mandated here, but if we chose to include an arbitration clause, it
                    would go here. As of now, we are defaulting to court resolution. If in the future we require arbitration, we will
                    update these Terms to reflect that along with any opt-out options.)
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Time Limit: </ColorText>
                    You agree that any cause of action arising out of or related to the Services must commence within
                    <ColorText color={theme.verascopeColor.purple500}> one (1) year  </ColorText>
                    after the cause of action accrues. Otherwise, such cause of action is permanently barred. (This does not apply to
                    intellectual property claims or statutory limitation periods which by law cannot be shortened.)
                </TermsText>
                <TermsHeader
                    marginTop
                    color={theme.verascopeColor.purple400}
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    13. Modifications to the Service and Terms
                </TermsHeader>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Service Changes: </ColorText>
                    Verascope is continually evolving. We reserve the right to
                    <ColorText color={theme.verascopeColor.purple500}> modify, update, or discontinue </ColorText>
                    the Service (or any part thereof) at any time, with or without notice. For example, we may add or
                    remove features, change the layout of the website, update the content, or suspend the site for
                    maintenance. We are not liable if for any reason any part of the Service is unavailable or altered
                    at any time. If a change is likely to significantly affect paid content (for example, if we were
                    to discontinue the digital book platform), we would attempt to notify affected users and provide
                    alternative access or compensation.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Terms Changes: </ColorText>
                    We may revise these Terms from time to time. If we make material changes, we will notify you by
                    posting the updated Terms on our site and updating the “Last Updated” date above. In some cases,
                    we may provide additional notice (such as an email notification or a pop-up on the site).
                    <ColorText color={theme.verascopeColor.purple500}>
                        {' '}
                        Your  continued use of the Services after changes to the Terms become effective constitutes your acceptance of the new Terms
                    </ColorText>
                    . If you do not agree to the revised Terms, you should stop using the Services and, if applicable, delete your account.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    For changes prompted by legal reasons, they may become effective immediately; for other changes, we will
                    give reasonable advance notice when possible. We encourage you to review the Terms periodically.
                </TermsText>
                <TermsHeader
                    marginTop
                    color={theme.verascopeColor.purple400}
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    14. Miscellaneous
                </TermsHeader>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Entire Agreement: </ColorText>
                    These Terms (along with the Privacy Policy and any additional guidelines or policies posted
                    on the site) constitute the
                    <ColorText color={theme.verascopeColor.purple500}> entire agreement </ColorText>
                    between you and Verascope regarding your use of the Services, and supersede any prior agreements or
                    understandings (whether oral or written).
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Waiver: </ColorText>
                    Our failure to enforce any right or provision of these Terms will not be considered a waiver of those
                    rights. Any waiver must be in writing and signed by an authorized representative of Verascope. If we
                    do waive a default or breach by you, that waiver does not mean we will automatically waive any later default or breach.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Severability: </ColorText>
                    If any provision of these Terms is held to be invalid or unenforceable by a court of competent jurisdiction,
                    that provision will be enforced to the maximum extent permissible and the remaining provisions of these Terms
                    will remain in full force and effect. We agree to negotiate in good faith a valid, enforceable substitute provision
                    that most nearly effects the parties’ intent in entering into these Terms.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Assignment: </ColorText>
                    You may not assign or transfer your rights or obligations under these Terms to anyone else without our prior
                    written consent. Verascope may assign its rights and obligations without restriction (for instance, in the case of
                    a corporate reorganization, merger, acquisition, or sale of assets).
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Third-Party Beneficiaries: </ColorText>
                    These Terms do not confer any third-party beneficiary rights. No one other than you and Verascope (and our
                    successors in interest) has any right to enforce any of these Terms.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Relationship of Parties: </ColorText>
                    You and Verascope are independent contractors. These Terms do not create any joint venture, partnership,
                    employment, or agency relationship between us.
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple400}>Headings: </ColorText>
                    Section headings in these Terms are for convenience only and have no legal or contractual effect.
                </TermsText>
                <TermsHeader
                    marginTop
                    color={theme.verascopeColor.purple400}
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    15. Contact Information
                </TermsHeader>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    If you have any questions about these Terms, or need to contact us for any reason, please reach out to:
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple500}>Verascope, Inc. </ColorText>
                    <br />
                    Email:
                    {' '}
                    <Anchor
                        className={HOVER_TARGET_CLASSNAME}
                        customCursorActive={customCursorActive}
                        href={`mailto:${SUPPORT_EMAIL_ADDRESS}`}
                        target="_blank"
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: (e) => onCursorEnter(
                                CURSOR_TARGET.email,
                                [CURSOR_SIGN.click],
                                e.currentTarget as HTMLElement,
                            ),
                            onMouseLeave: (e) => onCursorLeave(e),
                        })}
                    >
                        {SUPPORT_EMAIL_ADDRESS}
                    </Anchor>
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    We value our users and their experience. If you encounter any issues on the site or have feedback,
                    don’t hesitate to contact us. We’ll do our best to help!
                </TermsText>
                <TermsText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={TERMS_TEXT_DURATION}
                >
                    By using the Verascope website and Services, you acknowledge that you have read, understood, and
                    agree to these Terms of Use and our Privacy Policy. Thank you for being a part of the Verascope
                    community and happy reading!
                </TermsText>
            </TermsTextContainer>
        </Container>
    );
}

export default TermsView;
